import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import Styles from './ImageViewer.module.scss';
import closeIcon from 'assets/svg/close.svg';

interface ImageViewerProps {
  imageUrl: string;
  imageName?: string;
  onClose: () => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.2;

const ImageViewer: FunctionComponent<ImageViewerProps> = ({ imageUrl, imageName, onClose }) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // refs for smooth updates without re-rendering each event
  const scaleRef = useRef(1);
  const txRef = useRef(0);
  const tyRef = useRef(0);
  const isPanningRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const rafId = useRef<number | null>(null);
  const needsUpdate = useRef(false);

  // local state only to trigger initial layout / className
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // reset when image changes
  useEffect(() => {
    scaleRef.current = 1;
    txRef.current = 0;
    tyRef.current = 0;
    applyTransform();
    setTick(v => v + 1);
  }, [imageUrl]);

  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

  const scheduleUpdate = () => {
    if (needsUpdate.current) return;
    needsUpdate.current = true;
    rafId.current = requestAnimationFrame(() => {
      needsUpdate.current = false;
      applyTransform();
    });
  };

  const applyTransform = () => {
    if (!imgRef.current) return;
    const s = scaleRef.current;
    const tx = txRef.current;
    const ty = tyRef.current;
    imgRef.current.style.transform = `translate(${tx}px, ${ty}px) scale(${s})`;
  };

  // imperative wheel listener with passive:false
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const dir = e.deltaY > 0 ? -1 : 1;
      const next = clamp(scaleRef.current + dir * ZOOM_STEP, MIN_SCALE, MAX_SCALE);
      scaleRef.current = next;
      if (next === 1) { txRef.current = 0; tyRef.current = 0; }
      scheduleUpdate();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel as EventListener);
  }, []);

  const onDoubleClick: React.MouseEventHandler<HTMLDivElement> = () => {
    const next = scaleRef.current === 1 ? 2 : 1;
    scaleRef.current = next;
    if (next === 1) { txRef.current = 0; tyRef.current = 0; }
    scheduleUpdate();
  };

  const onMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (scaleRef.current === 1) return;
    isPanningRef.current = true;
    startXRef.current = e.clientX - txRef.current;
    startYRef.current = e.clientY - tyRef.current;
  };
  const onMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!isPanningRef.current) return;
    txRef.current = e.clientX - startXRef.current;
    tyRef.current = e.clientY - startYRef.current;
    scheduleUpdate();
  };
  const endPan = () => { isPanningRef.current = false; };

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => () => { if (rafId.current) cancelAnimationFrame(rafId.current); }, []);

  return (
    <div className={Styles.overlay} onClick={onBackdropClick} role="dialog" aria-modal="true">
      <div className={Styles.viewer}>
        <button className={Styles.closeBtn} onClick={onClose} aria-label="Close image viewer">
          <img src={closeIcon} alt="close" />
        </button>

        <div
          ref={wrapRef}
          className={`${Styles.imageWrap} ${scaleRef.current > 1 ? Styles.canPan : ''}`}
          onDoubleClick={onDoubleClick}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={endPan}
          onMouseLeave={endPan}
        >
          <img
            ref={imgRef}
            src={imageUrl}
            alt={imageName || 'image'}
            className={Styles.image}
            draggable={false}
          />
        </div>

        {imageName && <div className={Styles.caption}>{imageName}</div>}
      </div>
    </div>
  );
};

export default ImageViewer;


